import { css } from '@/styled-system/css';
import { Box, Button } from '@zillow/constellation-10';
import { useContext, useEffect, useMemo } from 'react';
import { useMessageListActions_GetConversationDataQuery } from '../../types/GraphqlTypes';
import { ModalType } from '../../types/ModalType';
import {
  emitDisplayRequestRenterProfileButtonEvent,
  emitRequestRenterProfileClickEvent,
  REQUEST_RENTER_PROFILE_CLICK_EVENT_TYPE_ID,
} from '../../constants/clickstreamEvents/requestRenterProfile';
import {
  CHOOSE_SAVED_REPLY_CLICK_EVENT_TYPE_ID,
  emitChooseSavedReplyClickEvent,
} from '../../constants/clickstreamEvents/savedReplyModal';
import StringConstants from '../../constants/StringConstants';
import useModal from '../../hooks/useModal';
import { AppContext } from '../../contexts/AppContextProvider';
import { InboxPageContext } from '../../contexts/InboxPageContextProvider';
import RequestRenterProfileModal from '../Modals/RequestRenterProfileModal';
import SavedReplyModal from '../Modals/SavedReplyModal';
import { TooltipWrapper } from '@zg-rentals/satellite-components';
import useConversationActions from '../../hooks/useConversationActions';
import { useAuth } from '@zg-rentals/auth-comet';

const pillButtonStyles = css.raw({
  borderRadius: 'pill',
  display: 'block',
  marginLeft: 'auto',
});

const MessageListActions = (): JSX.Element => {
  const { isImpersonator } = useContext(AppContext);
  const { clientTimeZone, conversationId, listingAlias } = useContext(InboxPageContext);
  const { activeModal, setActiveModal } = useModal();
  const { user } = useAuth();
  const { hasErrorOnRequestRenterProfile, requestRenterProfile } = useConversationActions();

  const { data: conversationData } = useMessageListActions_GetConversationDataQuery({
    variables: {
      clientTimeZone,
      conversationId: conversationId!,
      listingAlias: listingAlias!,
    },
    fetchPolicy: 'cache-only',
  });

  const { inquiry, isConversationBlockedByZillow, referenceName, renterProfile, status } =
    conversationData?.ZRM_getConversationById || {};
  const { shouldShowRequestRenterProfileCta } = renterProfile || {};
  const { isPhoneLead, name } = inquiry || {};
  const { isSpam } = status || {};
  const isChooseSavedReplyDisabled = Boolean(isImpersonator || isPhoneLead || isSpam || isConversationBlockedByZillow);

  useEffect(() => {
    if (shouldShowRequestRenterProfileCta) {
      emitDisplayRequestRenterProfileButtonEvent(user);
    }
  }, []);

  const chooseSavedReplyTooltipText = useMemo(() => {
    if (isChooseSavedReplyDisabled) {
      if (isPhoneLead) {
        return StringConstants.TOOLTIP.PHONE_LEAD;
      }
      if (isImpersonator) {
        return StringConstants.TOOLTIP.IMPERSONATION;
      }
      if (isSpam) {
        return StringConstants.TOOLTIP.REPORTED;
      }
      if (isConversationBlockedByZillow) {
        return StringConstants.TOOLTIP.BLOCKED_BY_ZILLOW;
      }
    }

    return '';
  }, [isChooseSavedReplyDisabled, isImpersonator, isPhoneLead, isSpam, isConversationBlockedByZillow]);

  const handleShowSavedReplyModal = (): void => {
    setActiveModal(ModalType.SavedReplyModal);
    emitChooseSavedReplyClickEvent(user);
  };

  const handleShowRequestRenterProfileModal = (): void => {
    setActiveModal(ModalType.RequestRenterProfileModal);
    emitRequestRenterProfileClickEvent(user);
  };

  return (
    <>
      {activeModal === ModalType.SavedReplyModal && <SavedReplyModal />}

      {activeModal === ModalType.RequestRenterProfileModal && (
        <RequestRenterProfileModal
          name={name || referenceName || ''}
          onRequestRenterProfileClick={requestRenterProfile}
          hasError={hasErrorOnRequestRenterProfile}
        />
      )}

      <Box
        css={{
          display: 'flex',
          flex: '0 0 auto',
          justifyContent: 'flex-end',
          paddingTop: 'tighter',
          paddingBottom: 'loose',
          marginTop: 'auto',
        }}
      >
        <div>
          <TooltipWrapper
            tooltipPlacement="top"
            wrapInTooltip={isChooseSavedReplyDisabled}
            tooltipText={chooseSavedReplyTooltipText}
          >
            <Button
              data-eventid={CHOOSE_SAVED_REPLY_CLICK_EVENT_TYPE_ID}
              aria-haspopup="true"
              aria-label="View saved reply messages"
              disabled={isChooseSavedReplyDisabled}
              emphasis="outlined"
              onClick={handleShowSavedReplyModal}
              tone="brand"
              css={pillButtonStyles}
            >
              Choose saved reply
            </Button>
          </TooltipWrapper>

          {shouldShowRequestRenterProfileCta && (
            <TooltipWrapper
              tooltipPlacement="top"
              wrapInTooltip={isImpersonator}
              tooltipText={StringConstants.TOOLTIP.IMPERSONATION}
            >
              <Button
                data-eventid={REQUEST_RENTER_PROFILE_CLICK_EVENT_TYPE_ID}
                aria-haspopup="true"
                disabled={isImpersonator}
                emphasis="outlined"
                onClick={handleShowRequestRenterProfileModal}
                tone="brand"
                css={{ ...pillButtonStyles, marginTop: 'tighter' }}
              >
                Request information from renter
              </Button>
            </TooltipWrapper>
          )}
        </div>
      </Box>
    </>
  );
};

export default MessageListActions;
